import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const PartnershipManager = () => {
  const [partnerships, setPartnerships] = useState({
    active: [],
    pending: [],
    requests: [],
    completed: []
  });
  const [activeTab, setActiveTab] = useState('active');
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newPartnershipData, setNewPartnershipData] = useState({
    partnerId: '',
    eventTitle: '',
    description: '',
    responsibilities: {
      self: '',
      partner: ''
    },
    revenueShare: 50,
    startDate: '',
    endDate: ''
  });

  // Mock data - replace with actual API calls
  const mockPartnerships = {
    active: [
      {
        id: 1,
        partnerName: 'Downtown Café',
        partnerType: 'business',
        partnerId: 101,
        eventTitle: 'Monthly Coffee & Business Networking',
        description: 'Joint networking event combining coffee tasting with business networking',
        status: 'active',
        revenueShare: 60, // This business gets 60%
        startDate: new Date('2025-01-01'),
        endDate: new Date('2025-12-31'),
        responsibilities: {
          self: 'Event promotion, attendee management, networking facilitation',
          partner: 'Venue, coffee service, catering'
        },
        metrics: {
          eventsHosted: 8,
          totalAttendees: 240,
          revenue: 4800,
          avgRating: 4.7
        },
        nextEvent: {
          id: 201,
          date: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000),
          attendees: 25,
          maxAttendees: 30
        }
      },
      {
        id: 2,
        partnerName: 'Fitness Plus Gym',
        partnerType: 'business',
        partnerId: 102,
        eventTitle: 'Wellness & Nutrition Workshop Series',
        description: 'Combined fitness and nutrition education events',
        status: 'active',
        revenueShare: 50,
        startDate: new Date('2024-06-01'),
        endDate: new Date('2025-06-01'),
        responsibilities: {
          self: 'Nutrition content, meal planning workshops, marketing',
          partner: 'Fitness training, venue, equipment'
        },
        metrics: {
          eventsHosted: 12,
          totalAttendees: 180,
          revenue: 3600,
          avgRating: 4.5
        },
        nextEvent: {
          id: 202,
          date: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000),
          attendees: 15,
          maxAttendees: 20
        }
      }
    ],
    pending: [
      {
        id: 3,
        partnerName: 'Art Gallery Central',
        partnerType: 'business',
        partnerId: 103,
        eventTitle: 'Wine & Art Exhibition Nights',
        description: 'Monthly wine tasting events during art exhibitions',
        status: 'pending_approval',
        revenueShare: 55,
        submittedDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
        responsibilities: {
          self: 'Wine selection, sommelier services, event coordination',
          partner: 'Gallery space, art exhibitions, invitations'
        }
      }
    ],
    requests: [
      {
        id: 4,
        requesterName: 'Tech Startup Hub',
        requesterType: 'business',
        requesterId: 104,
        proposedEvent: 'Pitch Night & Networking Mixer',
        description: 'Monthly startup pitch events with professional networking',
        message: 'We love your event hosting expertise and would like to partner for monthly pitch nights. We can provide the venue and startup community, while you handle the networking and catering aspects.',
        requestDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
        proposedRevenue: 45, // They get 45%, we get 55%
        status: 'new'
      },
      {
        id: 5,
        requesterName: 'Local Bookstore',
        requesterType: 'business',
        requesterId: 105,
        proposedEvent: 'Author Reading & Wine Night',
        description: 'Book launch events with wine pairings',
        message: 'We host regular author events and think adding wine pairings would elevate the experience. Would you be interested in a partnership?',
        requestDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
        proposedRevenue: 40,
        status: 'new'
      }
    ],
    completed: [
      {
        id: 6,
        partnerName: 'Summer Festival Organizers',
        partnerType: 'organization',
        partnerId: 106,
        eventTitle: 'Summer Food & Music Festival',
        description: 'Annual summer festival food vendor coordination',
        status: 'completed',
        duration: '3 months',
        completedDate: new Date('2024-09-30'),
        finalMetrics: {
          eventsHosted: 1,
          totalAttendees: 2500,
          revenue: 15000,
          rating: 4.9
        }
      }
    ]
  };

  useEffect(() => {
    fetchPartnerships();
  }, []);

  const fetchPartnerships = async () => {
    setLoading(true);
    try {
      // Replace with actual API call
      // const response = await api.get('/api/partnerships');
      // setPartnerships(response.data);
      setPartnerships(mockPartnerships);
    } catch (error) {    } finally {
      setLoading(false);
    }
  };

  const respondToPartnershipRequest = async (requestId, action, message = '') => {
    try {
      // Replace with actual API call
      // await api.post(`/api/partnerships/requests/${requestId}/respond`, {
      //   action,
      //   message
      // });

      // Update local state
      setPartnerships(prev => ({
        ...prev,
        requests: prev.requests.map(req => 
          req.id === requestId ? { ...req, status: action } : req
        )
      }));
    } catch (error) {    }
  };

  const createPartnershipProposal = async () => {
    if (!newPartnershipData.partnerId || !newPartnershipData.eventTitle) return;

    try {
      // Replace with actual API call
      // await api.post('/api/partnerships', newPartnershipData);

      setShowCreateModal(false);
      setNewPartnershipData({
        partnerId: '',
        eventTitle: '',
        description: '',
        responsibilities: { self: '', partner: '' },
        revenueShare: 50,
        startDate: '',
        endDate: ''
      });
      
      // Refresh partnerships
      fetchPartnerships();
    } catch (error) {    }
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const PartnershipCard = ({ partnership, type }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <Link 
              to={`/business/${partnership.partnerId || partnership.requesterId}`}
              className="text-lg font-semibold text-primary-600 hover:text-primary-800"
            >
              {partnership.partnerName || partnership.requesterName}
            </Link>
            <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs">
              {partnership.partnerType || partnership.requesterType}
            </span>
          </div>
          <h3 className="font-medium text-gray-900 mb-2">
            {partnership.eventTitle || partnership.proposedEvent}
          </h3>
          <p className="text-gray-600 text-sm">{partnership.description}</p>
        </div>
        
        <div className="text-right">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            partnership.status === 'active' ? 'bg-green-100 text-green-800' :
            partnership.status === 'pending_approval' ? 'bg-yellow-100 text-yellow-800' :
            partnership.status === 'completed' ? 'bg-blue-100 text-blue-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {partnership.status.replace('_', ' ')}
          </span>
        </div>
      </div>

      {/* Active Partnerships */}
      {type === 'active' && (
        <div className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Revenue Share:</span>
            <span className="font-medium">{partnership.revenueShare}% / {100 - partnership.revenueShare}%</span>
          </div>
          
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Events Hosted:</span>
              <span className="font-medium ml-2">{partnership.metrics.eventsHosted}</span>
            </div>
            <div>
              <span className="text-gray-600">Total Revenue:</span>
              <span className="font-medium ml-2">{formatCurrency(partnership.metrics.revenue)}</span>
            </div>
            <div>
              <span className="text-gray-600">Attendees:</span>
              <span className="font-medium ml-2">{partnership.metrics.totalAttendees}</span>
            </div>
            <div>
              <span className="text-gray-600">Rating:</span>
              <span className="font-medium ml-2">{partnership.metrics.avgRating}/5</span>
            </div>
          </div>

          {partnership.nextEvent && (
            <div className="bg-cyan-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-cyan-900">Next Event</p>
                  <p className="text-xs text-cyan-700">{formatDate(partnership.nextEvent.date)}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-cyan-700">
                    {partnership.nextEvent.attendees}/{partnership.nextEvent.maxAttendees} registered
                  </p>
                  <Link
                    to={`/events/${partnership.nextEvent.id}`}
                    className="text-xs text-cyan-600 hover:text-cyan-800 font-medium"
                  >
                    View Event
                  </Link>
                </div>
              </div>
            </div>
          )}

          <div className="pt-3 border-t border-gray-100">
            <div className="grid grid-cols-1 gap-2 text-xs">
              <div>
                <span className="font-medium text-gray-700">Your responsibilities:</span>
                <p className="text-gray-600 mt-1">{partnership.responsibilities.self}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">Partner responsibilities:</span>
                <p className="text-gray-600 mt-1">{partnership.responsibilities.partner}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pending Partnerships */}
      {type === 'pending' && (
        <div className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Submitted:</span>
            <span className="font-medium">{formatDate(partnership.submittedDate)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Revenue Share:</span>
            <span className="font-medium">{partnership.revenueShare}% / {100 - partnership.revenueShare}%</span>
          </div>
          <div className="text-sm text-gray-600">
            <span className="font-medium">Waiting for approval from {partnership.partnerName}</span>
          </div>
        </div>
      )}

      {/* Requests */}
      {type === 'requests' && (
        <div className="space-y-3">
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-sm text-gray-700">{partnership.message}</p>
          </div>
          
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Proposed Revenue Share:</span>
            <span className="font-medium">{100 - partnership.proposedRevenue}% / {partnership.proposedRevenue}%</span>
          </div>
          
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Received:</span>
            <span className="font-medium">{formatDate(partnership.requestDate)}</span>
          </div>

          {partnership.status === 'new' && (
            <div className="flex space-x-2 pt-2">
              <button
                onClick={() => respondToPartnershipRequest(partnership.id, 'accepted')}
                className="flex-1 bg-green-600 text-white py-2 px-3 rounded text-sm hover:bg-green-700"
              >
                Accept
              </button>
              <button
                onClick={() => respondToPartnershipRequest(partnership.id, 'declined')}
                className="flex-1 bg-red-600 text-white py-2 px-3 rounded text-sm hover:bg-red-700"
              >
                Decline
              </button>
              <Link
                to={`/partnerships/negotiate/${partnership.id}`}
                className="flex-1 bg-primary-600 text-white py-2 px-3 rounded text-sm hover:bg-primary-700 text-center"
              >
                Negotiate
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Completed Partnerships */}
      {type === 'completed' && (
        <div className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Duration:</span>
            <span className="font-medium">{partnership.duration}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Completed:</span>
            <span className="font-medium">{formatDate(partnership.completedDate)}</span>
          </div>
          
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Events:</span>
              <span className="font-medium ml-2">{partnership.finalMetrics.eventsHosted}</span>
            </div>
            <div>
              <span className="text-gray-600">Revenue:</span>
              <span className="font-medium ml-2">{formatCurrency(partnership.finalMetrics.revenue)}</span>
            </div>
            <div>
              <span className="text-gray-600">Attendees:</span>
              <span className="font-medium ml-2">{partnership.finalMetrics.totalAttendees}</span>
            </div>
            <div>
              <span className="text-gray-600">Rating:</span>
              <span className="font-medium ml-2">{partnership.finalMetrics.rating}/5</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const tabConfig = [
    { key: 'active', label: 'Active', count: partnerships.active.length },
    { key: 'pending', label: 'Pending', count: partnerships.pending.length },
    { key: 'requests', label: 'Requests', count: partnerships.requests.length },
    { key: 'completed', label: 'Completed', count: partnerships.completed.length }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Partnership Management</h2>
          <p className="text-gray-600 mt-1">Manage your business partnerships and co-hosted events</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700"
        >
          Propose Partnership
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
        {tabConfig.map(({ key, label, count }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === key
                ? 'bg-white text-primary-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {label}
            {count > 0 && (
              <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                activeTab === key ? 'bg-primary-100 text-primary-600' : 'bg-gray-200 text-gray-600'
              }`}>
                {count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div>
        {partnerships[activeTab].length > 0 ? (
          <div className="grid gap-6">
            {partnerships[activeTab].map((partnership) => (
              <PartnershipCard key={partnership.id} partnership={partnership} type={activeTab} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              {activeTab === 'active' ? 'No active partnerships' :
               activeTab === 'pending' ? 'No pending partnerships' :
               activeTab === 'requests' ? 'No partnership requests' :
               'No completed partnerships'}
            </h3>
            <p className="mt-2 text-gray-600">
              {activeTab === 'active' ? 'Start collaborating with other businesses to co-host events.' :
               activeTab === 'pending' ? 'Partnership proposals you\'ve sent will appear here.' :
               activeTab === 'requests' ? 'Partnership requests from other businesses will appear here.' :
               'Your completed partnerships will be archived here.'}
            </p>
          </div>
        )}
      </div>

      {/* Create Partnership Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-96 overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">Propose Partnership</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Partner Business ID
                </label>
                <input
                  type="text"
                  value={newPartnershipData.partnerId}
                  onChange={(e) => setNewPartnershipData(prev => ({...prev, partnerId: e.target.value}))}
                  placeholder="Enter business ID or search"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event Title
                </label>
                <input
                  type="text"
                  value={newPartnershipData.eventTitle}
                  onChange={(e) => setNewPartnershipData(prev => ({...prev, eventTitle: e.target.value}))}
                  placeholder="Monthly Wine & Art Night"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={newPartnershipData.description}
                  onChange={(e) => setNewPartnershipData(prev => ({...prev, description: e.target.value}))}
                  placeholder="Describe the partnership and events you'd like to co-host..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Your Responsibilities
                  </label>
                  <textarea
                    value={newPartnershipData.responsibilities.self}
                    onChange={(e) => setNewPartnershipData(prev => ({
                      ...prev,
                      responsibilities: {...prev.responsibilities, self: e.target.value}
                    }))}
                    placeholder="What you'll handle..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Partner Responsibilities
                  </label>
                  <textarea
                    value={newPartnershipData.responsibilities.partner}
                    onChange={(e) => setNewPartnershipData(prev => ({
                      ...prev,
                      responsibilities: {...prev.responsibilities, partner: e.target.value}
                    }))}
                    placeholder="What you'd like them to handle..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Revenue Share (Your percentage)
                </label>
                <div className="flex items-center space-x-4">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={newPartnershipData.revenueShare}
                    onChange={(e) => setNewPartnershipData(prev => ({...prev, revenueShare: parseInt(e.target.value)}))}
                    className="flex-1"
                  />
                  <span className="font-medium w-20">
                    {newPartnershipData.revenueShare}% / {100 - newPartnershipData.revenueShare}%
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={newPartnershipData.startDate}
                    onChange={(e) => setNewPartnershipData(prev => ({...prev, startDate: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={newPartnershipData.endDate}
                    onChange={(e) => setNewPartnershipData(prev => ({...prev, endDate: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={createPartnershipProposal}
                  className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700"
                >
                  Send Proposal
                </button>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnershipManager;